import React, { useEffect, useState } from "react"
import { Container, Row, Col, Card, CardBody, Table, Button } from "reactstrap"
import _ from "lodash"

import * as backend from "../../helpers/backend_helper"
import Toaster from "../../components/Common/Toaster"

// A clickable selectable chip (used instead of cramped dropdowns).
const Chip = ({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      border: `1px solid ${on ? "#2ea99b" : "#cdd5dd"}`,
      borderRadius: 14,
      padding: "3px 12px",
      margin: 3,
      fontSize: 12,
      fontWeight: 600,
      cursor: "pointer",
      background: on ? "#2ea99b" : "#fff",
      color: on ? "#fff" : "#6b7785",
    }}
  >
    {on ? "✓ " : ""}{label}
  </button>
)

// The reports a validator can be allowed to validate. Labels mirror the
// Performance Score headers.
const REPORTS = [
  { value: "dailymonitoring", label: "Daily Onsite" },
  { value: "dailydocument", label: "Daily Documents" },
  { value: "dailywork", label: "Daily Work" },
  { value: "monthlyonsite", label: "Monthly Onsite" },
  { value: "monthlydocument", label: "Monthly Documents" },
  { value: "monthlywork", label: "Monthly Work" },
]

// Validation config (admins): for each "View & Validate" user, choose which
// reports they may validate and for which submitters. Feeds report validation.
const ValidationConfig = (props: any) => {
  const { factory } = props
  const orgId = factory && factory.orgId
  const facId = factory && factory._id

  const [userList, setUserList] = useState<any[]>([])
  // config[validatorUserId] = { reports: string[], users: string[] }
  const [config, setConfig] = useState<{ [uid: string]: { reports: string[]; users: string[] } }>({})

  useEffect(() => {
    backend.getUserOrg(orgId).then(setUserList).catch(() => {})
  }, [orgId])

  useEffect(() => {
    const initial: any = {}
    ;((factory && factory.validationConfig) || []).forEach((e: any) => {
      initial[String(e.userId)] = { reports: e.reports || [], users: (e.users || []).map(String) }
    })
    setConfig(initial)
    // Seed only when the factory changes so edits aren't wiped on re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factory && factory._id])

  const activeTeams = (factory && factory.teams ? factory.teams : []).filter((t: any) => t.status !== false)

  // Validators = active team members whose factory access is "View & Validate".
  const validators = activeTeams
    .filter((t: any) => (t.permissions || []).includes("VALIDATE"))
    .map((t: any) => {
      const u = _.find(userList, ["_id", t.userId])
      return u ? { userId: String(t.userId), name: u.name, role: u.role } : null
    })
    .filter(Boolean) as any[]

  // Everyone (active) a validator can be assigned to validate for.
  const userOptions = activeTeams
    .map((t: any) => {
      const u = _.find(userList, ["_id", t.userId])
      return u ? { value: String(t.userId), label: `${u.name} (${u.role})` } : null
    })
    .filter(Boolean) as any[]

  const toggleVal = (uid: string, key: "reports" | "users", val: string) => {
    setConfig((c) => {
      const cur = c[uid] || { reports: [], users: [] }
      const list = (cur[key] || []) as string[]
      const next = list.includes(val) ? list.filter((v) => v !== val) : [...list, val]
      return { ...c, [uid]: { ...cur, [key]: next } }
    })
  }

  const submit = async () => {
    const validationConfig = validators
      .map((v) => ({
        userId: v.userId,
        reports: (config[v.userId] && config[v.userId].reports) || [],
        users: (config[v.userId] && config[v.userId].users) || [],
      }))
      .filter((e) => e.reports.length > 0 && e.users.length > 0)
    try {
      await backend.updateFactory(orgId, facId, { validationConfig })
      props.update && props.update()
      Toaster.success("Validation configuration saved")
    } catch (e) {
      Toaster.error(typeof e === "string" ? e : "Failed to save validation configuration")
    }
  }

  return (
    <Container fluid>
      <div className="text-muted" style={{ fontSize: 13, lineHeight: 1.7 }}>
        <p className="mb-0">
          Only users with the <strong>View &amp; Validate</strong> factory access appear here (set in
          Organization &rarr; Manage Access). For each of them, pick the <strong>reports they can validate</strong> and
          the <strong>users they can validate for</strong>. They&apos;ll only be able to validate/reject those reports
          for those people.
        </p>
      </div>
      <Row>
        <Col lg={12}>
          <Card>
            <CardBody>
              {validators.length === 0 ? (
                <div className="mw-empty-state">
                  No &ldquo;View &amp; Validate&rdquo; users yet. Give a user the <strong>View &amp; Validate</strong> access
                  level for this factory (Organization &rarr; Manage Access) and they&apos;ll show up here.
                </div>
              ) : (
                <div className="table-responsive">
                  <Table className="table mw-table table-bordered mb-0">
                    <thead>
                      <tr className="table-success">
                        <th style={{ minWidth: 180 }}>Validator</th>
                        <th style={{ minWidth: 300 }}>Reports they can validate</th>
                        <th style={{ minWidth: 300 }}>Users they can validate for</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validators.map((v) => {
                        const cur = config[v.userId] || { reports: [], users: [] }
                        return (
                          <tr key={v.userId}>
                            <th scope="row">
                              {v.name} <small className="text-muted">({v.role})</small>
                            </th>
                            <td>
                              <div style={{ display: "flex", flexWrap: "wrap" }}>
                                {REPORTS.map((r) => (
                                  <Chip
                                    key={r.value}
                                    label={r.label}
                                    on={(cur.reports || []).includes(r.value)}
                                    onClick={() => toggleVal(v.userId, "reports", r.value)}
                                  />
                                ))}
                              </div>
                            </td>
                            <td>
                              <div style={{ display: "flex", flexWrap: "wrap" }}>
                                {userOptions.length === 0 && <span className="text-muted" style={{ fontSize: 12 }}>No team members</span>}
                                {userOptions.map((o) => (
                                  <Chip
                                    key={o.value}
                                    label={o.label}
                                    on={(cur.users || []).includes(o.value)}
                                    onClick={() => toggleVal(v.userId, "users", o.value)}
                                  />
                                ))}
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
              {validators.length > 0 && (
                <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
                  <Button color="success" onClick={submit}>Save</Button>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}

export default ValidationConfig
